import React, { useState, useRef, useEffect, useMemo, useId } from "react";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import "./CommonFilterInput.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  InputAdornment,
  TextField,
  Popover,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import AccessibleSpinner from "../../common/AccessibleSpinner";
import SearchIcon from "../../../assets/search.svg";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
// --- Pure helpers to reduce component cognitive complexity ---
const LABEL_FORMS = {
  Members: { singular: "member", plural: "members" },
  Providers: { singular: "provider", plural: "providers" },
  Pharmacies: { singular: "pharmacy", plural: "pharmacies" },
  Programs: { singular: "program", plural: "programs" },
  Disposition: { singular: "disposition", plural: "dispositions" },
};

const computeDisplayValue = (label, placeholder, value, multiple, compact) => {
  if (!multiple) return value || "";
  const selected = Array.isArray(value) ? value : [];
  const count = selected.length;
  if (count === 0) return placeholder;

  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span>{label}</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#E3E8EF",
            color: "#121212",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {count}
        </span>
      </div>
    );
  }

  const forms = LABEL_FORMS[label] || {
    singular: label.endsWith("s")
      ? label.slice(0, -1).toLowerCase()
      : label.toLowerCase(),
    plural: label.toLowerCase() + (label.endsWith("s") ? "" : "s"),
  };
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return count === 1
    ? `${count} ${cap(forms.singular)} Selected`
    : `${count} ${cap(forms.plural)} Selected`;
};

const filterOptions = (options, searchTerm, searchResults) => {
  if (searchTerm && searchResults.length > 0) return searchResults;
  if (searchTerm)
    return options.filter((o) =>
      o.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  return options;
};

// useOutsideClose removed as Popover handles click-away

// Accessible, fully clickable "All" row for multi-select dropdowns
const SelectAllCheckbox = ({ label, value, options, onSelectAll, allId }) => {
  const total = options.length;
  const selectedCount = Array.isArray(value)
    ? options.filter((opt) => value.includes(opt)).length
    : 0;
  // Only check 'All' if all filtered options are selected and filteredOptions is not empty
  const allSelected = total > 0 && selectedCount === total;
  const indeterminate = selectedCount > 0 && selectedCount < total;
  const checkboxId =
    allId || `filter-${String(label).toLowerCase()}-all-checkbox`;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <label
      className="dropdown-option select-all"
      style={
        isMobile
          ? {
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: "none",
              padding: 0,
              background: "transparent",
              textAlign: "left",
            }
          : {
              display: "flex",
              alignItems: "center",
              padding: "8px 12px",
              cursor: "pointer",
              marginBottom: "4px",
              borderRadius: "4px",
              backgroundColor: "#403c6f",
              minHeight: "36px",
              boxSizing: "border-box",
              width: "100%",
              border: "none",
              textAlign: "left",
            }
      }
    >
      <span
        style={{
          color: "#fff",
          fontSize: "14px",
          fontWeight: 500,
          flexGrow: 1,
          cursor: "pointer",
        }}
      >
        All
      </span>
      <Checkbox
        checked={allSelected}
        indeterminate={indeterminate}
        size="small"
        onChange={(e) => {
          e.stopPropagation();
          onSelectAll();
        }}
        slotProps={{
          input: {
            id: checkboxId,
            "aria-label": `${label} - All`,
          },
        }}
        style={{ padding: "4px", marginLeft: "8px", color: "#fff" }}
      />
    </label>
  );
};

SelectAllCheckbox.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  options: PropTypes.arrayOf(PropTypes.string),
  onSelectAll: PropTypes.func,
  allId: PropTypes.string,
};

const customTooltipStyles = {
  tooltip: {
    background: "#403c6f",
    color: "#fff",
    fontSize: "13px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    padding: "8px 12px",
    fontWeight: 400,
    maxWidth: 260,
    lineHeight: 1.5,
    letterSpacing: "0.01em",
  },
  arrow: {
    color: "#403c6f",
  },
};

const OptionRow = ({
  option,
  index,
  multiple,
  isSelected,
  label,
  onToggle,
}) => {
  /* Logic extracted to OptionLabel */
  const optionContent = <OptionLabel option={option} label={label} />;
  const Component = multiple ? "label" : "button";

  let ariaChecked;
  let ariaSelected;
  let interactiveProps = {};

  if (multiple) {
    ariaChecked = isSelected ? "true" : "false";
    ariaSelected = undefined;
  } else {
    ariaChecked = undefined;
    ariaSelected = isSelected ? "true" : "false";
    interactiveProps = {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        onToggle(option);
      },
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle(option);
        }
      },
      role: "option",
      "aria-label": `${isSelected ? "Deselect" : "Select"} ${option}`,
    };
  }

  return (
    <Component
      key={option}
      {...interactiveProps}
      className={`dropdown-option ${isSelected ? "selected" : ""} ${
        index < 99999 ? "with-underline" : ""
      }`}
      style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        width: "100%",
        border: "none",
        padding: 0,
        background: "transparent",
        textAlign: "left",
      }}
      aria-checked={ariaChecked}
      aria-selected={ariaSelected}
    >
      {optionContent}
      {multiple && (
        <Checkbox
          checked={isSelected}
          size="small"
          onChange={(e) => {
            e.stopPropagation();
            onToggle(option);
          }}
          slotProps={{
            input: {
              id: `filter-${label.toLowerCase()}-checkbox-${index}`,
              "aria-label": option,
            },
          }}
          style={{ padding: "4px", marginLeft: "8px" }}
        />
      )}
    </Component>
  );
};

// Extracted helper logic
const toTitleCase = (str) =>
  str.replaceAll(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase(),
  );

function getCarrierGroupNameAndCode(opt) {
  if (typeof opt !== "string") return { name: "", code: "" };
  const close = opt.lastIndexOf(")");
  const open = opt.lastIndexOf("(", close);
  if (open !== -1 && close !== -1 && open < close) {
    return {
      name: opt.slice(0, open).trim(),
      code: opt.slice(open + 1, close).trim(),
    };
  }
  return { name: opt.trim(), code: "" };
}

function getDisplayName(name) {
  return name.length > 10 ? name.slice(0, 10) + "..." : name;
}

function getDisplayCode(code) {
  return code.length > 8 ? code.slice(0, 8) + "..." : code;
}

const formatCarrierGroupOption = (opt) => {
  const { name, code } = getCarrierGroupNameAndCode(opt);
  const titleName = toTitleCase(name);
  const displayName = getDisplayName(titleName);
  let display = displayName;
  let tooltip = titleName;
  if (code) {
    const displayCode = getDisplayCode(code);
    display += ` (${displayCode})`;
    tooltip += ` (${code})`;
  }
  return { display, tooltip };
};

const OptionLabel = ({ option, label }) => {
  if (label === "Carrier" || label === "Group") {
    const { display, tooltip } = formatCarrierGroupOption(option);
    return (
      <Tooltip
        title={tooltip}
        arrow
        placement="right"
        slotProps={{
          tooltip: { sx: customTooltipStyles.tooltip },
          arrow: { sx: customTooltipStyles.arrow },
        }}
      >
        <span
          className="option-text"
          style={{
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "180px",
          }}
          aria-label={option}
        >
          {display}
        </span>
      </Tooltip>
    );
  }

  if (label === "Assignee") {
    return (
      <Tooltip
        title={option}
        arrow
        placement="right"
        slotProps={{
          tooltip: { sx: customTooltipStyles.tooltip },
          arrow: { sx: customTooltipStyles.arrow },
        }}
      >
        <span
          className="option-text"
          style={{
            flexGrow: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "180px",
          }}
          aria-label={option}
        >
          {option}
        </span>
      </Tooltip>
    );
  }

  return (
    <span
      className="option-text"
      style={{
        flexGrow: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "180px",
      }}
      aria-label={option}
    >
      {label === "Program" ||
      label === "Programs" ||
      label === "Recent Activity" ||
      label === "Filter by Recent Activity"
        ? option
        : capitalizeFirstLetter(option)}
    </span>
  );
};

OptionLabel.propTypes = {
  option: PropTypes.string.isRequired,
  label: PropTypes.string,
};

OptionRow.propTypes = {
  option: PropTypes.string,
  index: PropTypes.number,
  multiple: PropTypes.bool,
  isSelected: PropTypes.bool,
  label: PropTypes.string,
  onToggle: PropTypes.func,
};

// --- Additional helpers to simplify main component ---
const getSearchPlaceholder = (label) => {
  const map = {
    Members: "Search member",
    Member: "Search member",
    Providers: "Search provider",
    Provider: "Search provider",
    Pharmacies: "Search pharmacy",
    Pharmacy: "Search pharmacy",
    Programs: "Search program",
    Program: "Search program",
  };
  return map[label] || "Search...";
};

// Hook encapsulating select dropdown state/logic
const useFilterSelect = ({
  multiple,
  options,
  value,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading = false,
  hasMore = false,
  filterType,
  autoPrefetch = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const isOpen = Boolean(anchorEl);

  const currentValues = multiple && Array.isArray(value) ? value : [];
  const clearAll = () => {
    // Only clear the filter if there is a selection; otherwise, just clear the search input
    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        onChange?.([]);
      }
    } else if (!multiple && value) {
      onChange?.("");
    }
    setSearchTerm("");
    setInputValue("");
    // Always reset the dropdown options to default when clearing
    onSearch?.("", filterType);
  };
  const toggleDropdown = (e) => {
    if (e?.target?.closest?.(".dropdown-icon")) clearAll();
    if (isOpen && !searchTerm) setInputValue("");

    if (isOpen) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.currentTarget);
      onOpen?.(filterType);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Only clear the input value if there is no search term
    if (!searchTerm) {
      setInputValue("");
    }
  };
  const selectAll = () => {
    // Only operate on currently filtered options (search results)
    const filtered = filterOptions(options, searchTerm, searchResults);
    if (filtered.length === 0) return;
    const allFilteredSelected = filtered.every((opt) =>
      currentValues.includes(opt),
    );
    if (allFilteredSelected) {
      // Deselect only the filtered options, keep others selected
      const newValues = currentValues.filter((v) => !filtered.includes(v));
      onChange?.(newValues);
    } else {
      // Select all filtered options, keep others selected
      const newSet = new Set([...currentValues, ...filtered]);
      onChange?.(Array.from(newSet));
    }
  };
  const commitSearch = () => {
    const term = inputValue.trim();
    if (term) {
      setIsSearching(true);
      setSearchTerm(term);
      onSearch?.(term, filterType);
      setTimeout(() => setIsSearching(false), 300);
    } else {
      setSearchTerm("");
      setSearchResults([]);
      onSearch?.("", filterType);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitSearch();
    }
  };
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (!val.trim()) {
      setSearchTerm("");
      setSearchResults([]);
      onSearch?.("", filterType);
    }
  };
  const handleOptionClick = (option) => {
    if (!multiple) {
      onChange?.(option);
      handleClose();
      return;
    }
    const newValues = currentValues.includes(option)
      ? currentValues.filter((v) => v !== option)
      : [...currentValues, option];
    onChange?.(newValues);
    onChange?.(newValues);
    // Keep open for multiple selection
  };
  const filteredOptions = useMemo(() => {
    const list = filterOptions(options, searchTerm, searchResults);
    if (!value || (multiple && (!Array.isArray(value) || value.length === 0))) {
      return list;
    }
    const selected = [];
    const unselected = [];
    if (multiple) {
      const currentValues = new Set(value);
      const listSet = new Set(list);
      // [...value].reverse() ensures the most recently selected is first
      selected.push(...[...value].reverse().filter((opt) => listSet.has(opt)));
      for (const opt of list) {
        if (!currentValues.has(opt)) unselected.push(opt);
      }
    } else {
      for (const opt of list) {
        if (opt === value) selected.push(opt);
        else unselected.push(opt);
      }
    }
    return [...selected, ...unselected];
  }, [options, searchTerm, searchResults, multiple, value]);

  useEffect(() => {
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    const onScroll = (el) => {
      if (loadingMoreRef.current) return;
      if (isSearching || isLoading) return;
      if (!hasMore) return;
      const threshold = 100;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        loadingMoreRef.current = true;
        void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
          loadingMoreRef.current = false;
        });
      }
    };
    const menuHandler = () => onScroll(menuEl);
    const listHandler = () => onScroll(listEl);
    if (menuEl)
      menuEl.addEventListener("scroll", menuHandler, { passive: true });
    if (listEl)
      listEl.addEventListener("scroll", listHandler, { passive: true });
    return () => {
      if (menuEl) menuEl.removeEventListener("scroll", menuHandler);
      if (listEl) listEl.removeEventListener("scroll", listHandler);
    };
  }, [isOpen, isSearching, isLoading, hasMore, onLoadMore, filterType]);

  // If the first page doesn't overflow (not enough items to scroll),
  // prefetch next page(s) until we can scroll or there are no more items.
  useEffect(() => {
    if (!autoPrefetch) return;
    if (!isOpen) return;
    const menuEl = dropdownMenuRef.current;
    const listEl = optionsRef.current;
    if (!menuEl && !listEl) return;
    if (loadingMoreRef.current) return;
    if (isLoading) return;
    if (!hasMore) return;
    const menuNeeds = menuEl
      ? menuEl.scrollHeight <= menuEl.clientHeight
      : false;
    const listNeeds = listEl
      ? listEl.scrollHeight <= listEl.clientHeight
      : false;
    if (menuNeeds || listNeeds) {
      loadingMoreRef.current = true;
      void Promise.resolve(onLoadMore?.(filterType)).finally(() => {
        loadingMoreRef.current = false;
      });
    }
  }, [
    autoPrefetch,
    isOpen,
    hasMore,
    isLoading,
    onLoadMore,
    filterType,
    filteredOptions.length,
  ]);
  return {
    dropdownRef,
    optionsRef,
    anchorEl,
    isOpen,
    toggleDropdown,
    handleClose,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading,
    filteredOptions,
    handleOptionClick,
    selectAll,
    hasMore,
  };
};

// Select variant component
const FilterSelect = ({
  label,
  placeholder,
  value,
  multiple,
  options,
  onChange,
  onSearch,
  onOpen,
  onLoadMore,
  isLoading,
  hasMore,
  filterType,
  autoPrefetch = true,
  instanceId,
  compact,
}) => {
  const {
    dropdownRef,
    optionsRef,
    anchorEl,
    isOpen,
    toggleDropdown,
    handleClose,
    inputValue,
    handleInputChange,
    handleKeyDown,
    commitSearch,
    isSearching,
    isLoading: isLoadingFromHook,
    filteredOptions,
    handleOptionClick,
    selectAll,
  } = useFilterSelect({
    multiple,
    options,
    value,
    onChange,
    onSearch,
    onOpen,
    onLoadMore,
    isLoading,
    hasMore,
    filterType,
    autoPrefetch,
  });

  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const displayId = `${idBase}-input-display`;
  const menuId = `${idBase}-menu`;
  const labelId = `${idBase}-label`;
  const allCheckboxId = `${idBase}-all-checkbox`;

  const displayValue = useMemo(
    () => computeDisplayValue(label, placeholder, value, multiple, compact),
    [label, placeholder, value, multiple, compact],
  );
  const searchPlaceholder = getSearchPlaceholder(label);
  return (
    <div className="common-filter-input" ref={dropdownRef}>
      <label className="filter-label" htmlFor={displayId} id={labelId}>
        {label}
      </label>
      <div className="filter-input-container">
        <button
          id={displayId}
          type="button"
          className="filter-input"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={menuId}
          aria-label={`${label}: ${displayValue}`}
          onClick={toggleDropdown}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleDropdown(e);
            }
          }}
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            paddingRight: { xs: "32px", md: "12px" },
            border: "none",
            background: "transparent",
            width: "100%",
            textAlign: "left",
            fontSize: "inherit",
            fontFamily: "inherit",
            color: "inherit",
          }}
        >
          {displayValue}
        </button>
        {isOpen ? (
          <IconButton
            className="dropdown-icon"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleDropdown(e);
            }}
            aria-label={`Close ${label} filter`}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#666",
              padding: "4px",
            }}
          >
            <CloseIcon style={{ fontSize: "20px" }} />
          </IconButton>
        ) : (
          <ExpandMoreIcon
            className="dropdown-icon"
            viewBox="0 0 24 24"
            style={{ transition: "transform 0.2s" }}
          />
        )}
        <Popover
          id={menuId}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              className: `dropdown-menu ${multiple ? "multiple" : ""}`,
              role: "group",
              "aria-label": `${label} filter options`,
              sx: {
                backgroundColor: "#6E6D92",
                color: "#fff",
                borderRadius: "8px",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                minWidth: anchorEl ? anchorEl.clientWidth : 280,
                mt: 1,
                maxHeight: 300,
                overflowY: "auto",
              },
            },
          }}
        >
          <div className="options-container" ref={optionsRef}>
            <div className="search-option">
              <TextField
                type="text"
                placeholder={searchPlaceholder}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="search-option-input"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#92A0B3",
                    },
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {isSearching ? (
                          <AccessibleSpinner size={20} label="Searching" />
                        ) : (
                          <IconButton
                            onClick={commitSearch}
                            aria-label="Execute search"
                            size="small"
                            sx={{
                              padding: 0,
                              "&:hover": { backgroundColor: "transparent" },
                            }}
                          >
                            <img
                              src={SearchIcon}
                              alt=""
                              style={{
                                width: "20px",
                                height: "20px",
                                marginRight: "8px",
                              }}
                            />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                    style: { paddingRight: "8px" },
                    sx: { "&:focus": { outline: "none", boxShadow: "none" } },
                  },
                }}
              />
            </div>
            {multiple && filteredOptions.length > 0 && (
              <SelectAllCheckbox
                label={label}
                value={value}
                options={filteredOptions}
                onSelectAll={selectAll}
                allId={allCheckboxId}
              />
            )}
            {filteredOptions.map((option, idx) => (
              <OptionRow
                key={option}
                option={option}
                index={idx}
                multiple={multiple}
                label={label}
                isSelected={
                  multiple
                    ? Array.isArray(value) && value.includes(option)
                    : value === option
                }
                onToggle={handleOptionClick}
              />
            ))}
            {isLoadingFromHook || isLoading ? (
              // Show spinner while loading
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "8px 0",
                }}
              >
                <AccessibleSpinner
                  size={20}
                  label="Loading options"
                  sx={{ color: "#fff" }}
                />
              </div>
            ) : (
              filteredOptions.length === 0 && (
                <div className="no-options">No options available</div>
              )
            )}
          </div>
        </Popover>
      </div>
    </div>
  );
};

FilterSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  instanceId: PropTypes.string,
  compact: PropTypes.bool,
};

// Text variant component
const FilterText = ({ label, placeholder, value, onChange, instanceId }) => {
  const uidHook = useId();
  const uid = instanceId || uidHook;
  const idBase = `filter-${label.toLowerCase()}-${uid}`;
  const inputId = `${idBase}-text-input`;
  return (
    <div className="common-filter-input">
      <label className="filter-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="filter-input-container">
        <input
          type="text"
          className="filter-input"
          placeholder={placeholder}
          value={value}
          id={inputId}
          onChange={(e) => onChange?.(e.target.value)}
          style={{ padding: "8px 12px" }}
        />
        <svg
          className="dropdown-icon"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
    </div>
  );
};

FilterText.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  instanceId: PropTypes.string,
};

const CommonFilterInput = (props) => {
  const { type = "text" } = props;
  return type === "select" ? (
    <FilterSelect {...props} />
  ) : (
    <FilterText {...props} />
  );
};

export default CommonFilterInput;

CommonFilterInput.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(["text", "select"]),
  multiple: PropTypes.bool,
  onSearch: PropTypes.func,
  onOpen: PropTypes.func,
  onLoadMore: PropTypes.func,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  filterType: PropTypes.string,
  autoPrefetch: PropTypes.bool,
  instanceId: PropTypes.string,
  compact: PropTypes.bool,
};
